import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { useLoaderData } from "@remix-run/react";
import type { SanityDocument } from "@sanity/client";
import clsx from "clsx";
import Footer from "~/components/Footer";
import Header from "~/components/Header";
import BlogHero from "~/components/blog/BlogHero";
import ZestPortableText from "~/components/blog/ZestPortableText";
import { blogPostQueryDynamic, employeeQuery } from "~/groq";
import { useBlogPostPublishDate } from "~/hooks/useBlogPostPublishDate";
import { getClient } from "~/sanity";

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const query = blogPostQueryDynamic(params.post || "");
  const posts = await getClient().fetch(query);
  const authors = await getClient().fetch(employeeQuery);
  const author = authors.filter(
    (author: SanityDocument) => author._id === posts[0].author._ref
  )[0];
  const post = {
    ...posts[0],
    author,
  };

  return { post };
};

export const meta: MetaFunction = ({ data }) => {
  const { post } = data;
  return [
    { title: `${post.title} | Zest` },
    { name: "og:title", content: `${post.title} | Zest` },
    { name: "description", content: "The people cooking up Zest" },
    { name: "og:description", content: "The people cooking up Zest" },
  ];
};

export default function BlogPost() {
  const { post } = useLoaderData<typeof loader>();
  const formatPublishDate = useBlogPostPublishDate();
  const formattedPublishDate = formatPublishDate(post.publishDate);

  return (
    <main className="overflow-hidden text-text-black">
      <Header />
      <BlogHero
        author={post.author}
        date={formattedPublishDate}
        title={post.title}
      />
      <div
        className={clsx([
          "mx-auto mt-10 grid max-w-4xl px-6 pb-56",
          "mx-12 md:mt-20 md:px-2",
        ])}
      >
        <ZestPortableText blocks={post.content} />
      </div>
      <Footer />
    </main>
  );
}
